import React, { useState, useMemo } from "react";
import {
  Box,
  Button,
  Card,
  CardBody,
  Text,
  Flex,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  HStack,
  VStack,
  Avatar,
  Badge,
  Progress,
  Select,
  Divider,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useColorModeValue,
  Tooltip,
  Spinner,
} from "@chakra-ui/react";
import {
  FaEye,
  FaEdit,
  FaTrash,
  FaEllipsisV,
  FaSearch,
  FaDownload,
  FaFilter,
  FaUsers,
  FaBuilding,
} from "react-icons/fa";

function UserTable({ data = [], isLoading = false }) {
  // Color mode values
  const bg = useColorModeValue("gray.50", "gray.700");
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const textColor = useColorModeValue("gray.800", "white");
  const mutedColor = useColorModeValue("gray.600", "gray.400");

  // State management
  const [currentPage, setCurrentPage] = useState(1);
  const [itemPerPage, setItemPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");

  // Utility functions
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getVerifyText = (verified) => {
    switch (verified) {
      case true: return "Premium";
      case false: return "Free Trial";
      case "active": return "Active";
      case "inactive": return "Inactive";
      default: return "Unknown";
    }
  };

  const getStatusColorScheme = (verified) => {
    switch (verified) {
      case true: return "yellow";
      case false: return "blue";
      case "active": return "green";
      case "inactive": return "red";
      default: return "gray";
    }
  };

  // Filter and sort data
  const filteredAndSortedData = useMemo(() => {
    let filtered = data.filter(item =>
      item.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.employee_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.company_email?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (sortField) {
      filtered.sort((a, b) => {
        let aValue = a[sortField] || "";
        let bValue = b[sortField] || "";

        if (typeof aValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        if (sortDirection === "asc") {
          return aValue > bValue ? 1 : -1;
        } else {
          return aValue < bValue ? 1 : -1;
        }
      });
    }

    return filtered;
  }, [data, searchTerm, sortField, sortDirection]);

  // Pagination calculations
  const indexOfLastItem = currentPage * itemPerPage;
  const indexOfFirstItem = indexOfLastItem - itemPerPage;
  const UsedData = filteredAndSortedData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredAndSortedData.length / itemPerPage);

  // Handle sort
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Render table data
  const renderData = () => {
    if (isLoading) {
      return (
        <Tr>
          <Td colSpan={9} textAlign="center" py={8}>
            <VStack spacing={3}>
              <Spinner size="lg" color="blue.500" />
              <Text color={mutedColor}>Loading data...</Text>
            </VStack>
          </Td>
        </Tr>
      );
    }

    if (UsedData.length === 0) {
      return (
        <Tr>
          <Td colSpan={9} textAlign="center" py={8}>
            <VStack spacing={3}>
              <FaUsers size={40} color="#CBD5E0" />
              <Text color={mutedColor}>No data available</Text>
            </VStack>
          </Td>
        </Tr>
      );
    }

    return UsedData.map((item, index) => (
      <Tr
        key={item.id || index}
        _hover={{ bg: bg }}
        transition="all 0.2s"
      >
        <Td>
          <Text fontSize="sm" fontWeight="medium" color={textColor}>
            {indexOfFirstItem + index + 1}
          </Text>
        </Td>

        <Td>
          <HStack spacing={3}>
            <Avatar
              size="sm"
              name={item.employee_name || "Unknown User"}
              bg="blue.400"
              color="white"
            />
            <VStack align="start" spacing={0}>
              <Text fontSize="sm" fontWeight="medium" color={textColor}>
                {item.employee_name || "-"}
              </Text>
              <Text fontSize="xs" color={mutedColor}>
                ID: {item.id || "N/A"}
              </Text>
            </VStack>
          </HStack>
        </Td>

        <Td>
          <VStack align="start" spacing={1}>
            <HStack>
              <FaBuilding size={12} color="#718096" />
              <Text fontSize="sm" fontWeight="medium" color={textColor}>
                {item.company_name}
              </Text>
            </HStack>
            <Text fontSize="xs" color={mutedColor}>
              {item.company_email}
            </Text>
          </VStack>
        </Td>

        <Td>
          <Badge
            colorScheme={getStatusColorScheme(item.verified)}
            variant="subtle"
            px={3}
            py={1}
            borderRadius="full"
            fontSize="xs"
            fontWeight="600"
          >
            {getVerifyText(item.verified)}
          </Badge>
        </Td>

        <Td>
          <VStack align="start" spacing={2}>
            <HStack spacing={2}>
              <Text fontSize="sm" fontWeight="medium" color={textColor}>
                {item.total_employee_registered || 0}
              </Text>
              <Text fontSize="xs" color={mutedColor}>
                / {item.total_employee || 0}
              </Text>
            </HStack>
            <Progress
              value={((item.total_employee_registered || 0) / (item.total_employee || 1)) * 100}
              size="sm"
              colorScheme="blue"
              borderRadius="full"
              width="80px"
            />
          </VStack>
        </Td>

        <Td>
          <Text fontSize="sm" color={textColor}>
            {formatDate(item.created_at)}
          </Text>
        </Td>

        <Td>
          <Text fontSize="sm" color={item.company_expired ? textColor : mutedColor}>
            {formatDate(item.company_expired)}
          </Text>
        </Td>

        <Td>
          <Badge
            colorScheme={getStatusColorScheme(item.status)}
            variant="solid"
            px={3}
            py={1}
            borderRadius="full"
            fontSize="xs"
            fontWeight="600"
          >
            {getVerifyText(item.status)}
          </Badge>
        </Td>

        <Td>
          <Menu>
            <MenuButton
              as={IconButton}
              icon={<FaEllipsisV />}
              variant="ghost"
              size="sm"
              _hover={{ bg: bg }}
            />
            <MenuList>
              <MenuItem icon={<FaEye />} fontSize="sm">
                View Details
              </MenuItem>
              <MenuItem icon={<FaEdit />} fontSize="sm">
                Edit
              </MenuItem>
              <MenuItem icon={<FaTrash />} fontSize="sm" color="red.500">
                Delete
              </MenuItem>
            </MenuList>
          </Menu>
        </Td>
      </Tr>
    ));
  };

  // Render pagination
  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <Button
          key={i}
          size="sm"
          variant={currentPage === i ? "solid" : "outline"}
          colorScheme={currentPage === i ? "blue" : "gray"}
          onClick={() => setCurrentPage(i)}
          minW="32px"
        >
          {i}
        </Button>
      );
    }

    return (
      <HStack spacing={1}>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
          isDisabled={currentPage === 1}
        >
          Previous
        </Button>
        {pages}
        <Button
          size="sm"
          variant="outline"
          onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
          isDisabled={currentPage === totalPages}
        >
          Next
        </Button>
      </HStack>
    );
  };

  return (
    <Card bg={cardBg} shadow="lg" borderRadius="xl" overflow="hidden">
      {/* Header */}
      <Box bg="gradient-to-r from-blue-600 to-purple-600" p={6}>
        <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
          <VStack align="start" spacing={1}>
            <Text fontSize="xl" fontWeight="bold" color="black">
              Customer Registration Overview
            </Text>
            <Text fontSize="sm" color="black">
              Manage and monitor company customer data
            </Text>
          </VStack>

          <HStack spacing={3}>
            <InputGroup size="md" maxW="300px" color="black">
              <InputLeftElement pointerEvents="none">
                <FaSearch color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Search customers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                bg="whiteAlpha.200"
                color="black"
                _placeholder={{ color: "black" }}
                _focus={{ bg: "whiteAlpha.300" }}
                borderWidth={"1px"}
                borderColor={"blue.400"}
              />
            </InputGroup>

            <Tooltip label="Export Data">
              <IconButton
                icon={<FaDownload />}
                size="md"
                variant="solid"
                color={"white"}
                bg="#252435ff"
                _hover={{ bg: "#373643ff" }}
              />
            </Tooltip>

            <Tooltip label="View Details">
              <IconButton
                icon={<FaEye />}
                color={"white"}
                variant="solid"
                bg="#238FBA"
                _hover={{ bg: "#6295a9ff" }}
                onClick={() => window.location.href = "/manage-user"}
              />
            </Tooltip>
          </HStack>
        </Flex>
      </Box>

      {/* Table */}
      <CardBody p={0}>
        <TableContainer>
          <Table variant="simple" size="sm">
            <Thead bg={bg}>
              <Tr>
                <Th fontSize="xs" fontWeight="bold" color={mutedColor}>#</Th>
                <Th
                  fontSize="xs"
                  fontWeight="bold"
                  color={mutedColor}
                  cursor="pointer"
                  onClick={() => handleSort('employee_name')}
                  _hover={{ color: textColor }}
                >
                  Customer
                </Th>
                <Th
                  fontSize="xs"
                  fontWeight="bold"
                  color={mutedColor}
                  cursor="pointer"
                  onClick={() => handleSort('company_name')}
                  _hover={{ color: textColor }}
                >
                  Company
                </Th>
                <Th fontSize="xs" fontWeight="bold" color={mutedColor}>Account Type</Th>
                <Th fontSize="xs" fontWeight="bold" color={mutedColor}>Members</Th>
                <Th
                  fontSize="xs"
                  fontWeight="bold"
                  color={mutedColor}
                  cursor="pointer"
                  onClick={() => handleSort('created_at')}
                  _hover={{ color: textColor }}
                >
                  Joined
                </Th>
                <Th fontSize="xs" fontWeight="bold" color={mutedColor}>Expires</Th>
                <Th fontSize="xs" fontWeight="bold" color={mutedColor}>Status</Th>
                <Th fontSize="xs" fontWeight="bold" color={mutedColor}>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {renderData()}
            </Tbody>
          </Table>
        </TableContainer>

        {/* Pagination Footer */}
        {!isLoading && UsedData.length > 0 && (
          <Box p={4} borderTop="1px" borderColor={borderColor}>
            <Flex
              justify="space-between"
              align="center"
              direction={{ base: "column", md: "row" }}
              gap={4}
            >
              <HStack spacing={2}>
                <Text fontSize="sm" color={mutedColor}>
                  Showing {indexOfFirstItem + 1} to{" "}
                  {Math.min(indexOfLastItem, filteredAndSortedData.length)} of {filteredAndSortedData.length} results
                </Text>
                <Divider orientation="vertical" h="20px" />
                <HStack spacing={2}>
                  <Text fontSize="sm" color={mutedColor}>Show</Text>
                  <Select
                    size="sm"
                    w="70px"
                    value={itemPerPage}
                    onChange={(e) => setItemPerPage(Number(e.target.value))}
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </Select>
                  <Text fontSize="sm" color={mutedColor}>per page</Text>
                </HStack>
              </HStack>

              {renderPagination()}
            </Flex>
          </Box>
        )}
      </CardBody>
    </Card>
  );
}

export default UserTable;